import type { ErrorDetail, TagTone } from '@dayon/design-system';
import { formatCount, formatPesos, formatPesosCompact } from '@dayon/money';
import {
  AUDIT_REASON_MIN_LENGTH,
  PAYOUT_STATE_WORDS,
  type FundsSummary,
  type ParkedReason,
  type PayoutAnswerSource,
  type PayoutAttemptRow,
  type PayoutProviderStatus,
  type PayoutState,
  type ReleaseOutcome,
  type WithdrawalPayout,
  type WithdrawalRow,
} from '@dayon/contracts';

/**
 * WHAT THE FUNDS SCREEN SAYS ABOUT A PAYOUT ONCE IT IS RELEASED.
 *
 * The words for a state and for a parked reason are the contract's
 * (`PAYOUT_STATE_WORDS`, `PARKED_REASON_WORDS` in @dayon/contracts), imported
 * rather than restated, so the console and the player's screens can never
 * call one state two things. What lives here is the console's own reading of
 * them: which tone a state is drawn in, which label a queue row carries, what
 * stage a request is in and what that stage is called, which of the operator's
 * five actions a parked payout allows, what each action says before it is
 * pressed, how an action's outcome is told, what each hand-over's latest
 * answer reads as, what the Payout panel gives as a payout's cause, its
 * StarPay and InstaPay references, its fee and the note on asks that brought
 * nothing usable, and what the "account holder's name" check reports — and
 * the two things every time and every reason on the funds screen share: the
 * stamp and the reason's floor.
 *
 * Kept apart from `Withdrawals.tsx` so the rules can be tested without
 * rendering a 4,000-line screen, and so the Payout panel and the queue column
 * read one copy of them.
 */

export type UnparkAction = 'QUERY' | 'CONFIRM_PAID' | 'REFUND' | 'RESEND_SAME_ID' | 'DISMISS_HOLD';

/**
 * The marker the risk job stamps on a request a critical alert holds, followed by the alert's
 * reference (`RISK_ALERT_HOLD_PREFIX` in @dayon/db, written by sql/240). Restated rather than
 * imported, because a console does not import the database package.
 */
const RISK_ALERT_MARKER = 'RISK_ALERT';

/**
 * A request's risk hold in words. The Risk column and the drawer printed the stored text, so a hold
 * placed by an alert read "RISK_ALERT RSK-2167" (module walk, 30 Oct 2026). It now reads as a hold by
 * that alert, keeping the reference an operator looks up on Risk; a hold recorded in words when the
 * request was made ("Payout ratio above 0.95") is passed on as it is.
 */
export function holdWords(heldReason: string): string {
  if (!heldReason.startsWith(RISK_ALERT_MARKER)) return heldReason;
  const ref = heldReason.slice(RISK_ALERT_MARKER.length).trim();
  return ref ? `Held by risk alert ${ref}` : 'Held by a risk alert';
}

/** "29 Aug, 21:12" — the funds screen's own format, 24-hour, so every time on it reads alike. */
export const stamp = new Intl.DateTimeFormat('en-PH', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * "29 Aug 2026, 21:12" — the same form with the year, for a list that runs back further than a year
 * can be taken for granted: the Payout accounts tab lists every account since the first.
 */
export const datedStamp = new Intl.DateTimeFormat('en-PH', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** "21:12" — the time of day alone, in the same 24-hour form, for what happened today. */
export const clock = new Intl.DateTimeFormat('en-PH', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * THE PAYOUT PROVIDER TILE'S FIGURE: the float StarPay last reported — or, under the mock driver,
 * "Mock driver", because the float there is the mock's own figure and printed bare it reads as
 * StarPay's money. With no provider configured there is no float to show at all.
 */
export function providerFigure(payouts: Pick<FundsSummary['payouts'], 'provider' | 'floatCentavos'>): string {
  if (payouts.provider === 'OFF') return 'Not configured';
  if (payouts.provider === 'MOCK') return 'Mock driver';
  return payouts.floatCentavos ? formatPesosCompact(BigInt(payouts.floatCentavos)) : 'Float unknown';
}

/**
 * THE PAYOUT PROVIDER TILE'S HEALTH LINE: the provider's connection check, made when its status
 * was read, and when it was made.
 *
 * One check says whether StarPay answered at that moment and nothing about since when, so the line
 * never says "since". Under the mock driver StarPay is not asked at all, so the line says it was
 * the mock that answered. With nothing checked (no provider is configured) the line is the API's
 * own words, `health.words`. "Not checked yet" would suggest a provider that is configured and
 * not yet asked, and "unreachable" would be a claim about a provider nobody asked.
 */
export function healthLine(status: Pick<PayoutProviderStatus, 'driverName' | 'health'>): string {
  const { reachable, checkedAt, words } = status.health;
  if (reachable === null || checkedAt === null) return words;
  const at = clock.format(new Date(checkedAt));
  if (status.driverName === 'MOCK') {
    return `${reachable ? 'Mock driver answered' : 'Mock driver did not answer'} · checked ${at}`;
  }
  return `StarPay ${reachable ? 'reachable' : 'unreachable'} · checked ${at}`;
}

/**
 * What a reason field says while its reason is too short — the database's own floor
 * (AUDIT_REASON_MIN_LENGTH), so the screen never accepts what the platform will refuse.
 * Nothing until somebody has started: an error on an untouched field is a form telling
 * somebody off for not having typed yet. One sentence for every reason on the funds screen.
 */
export function reasonProblem(reason: string): string | undefined {
  return reason.length > 0 && reason.trim().length < AUDIT_REASON_MIN_LENGTH
    ? `At least ${AUDIT_REASON_MIN_LENGTH} characters — it goes on the audit trail.`
    : undefined;
}

/** Whether a reason can be sent: the floor above, and the 500 the contract allows. */
export const reasonReady = (reason: string): boolean =>
  reason.trim().length >= AUDIT_REASON_MIN_LENGTH && reason.trim().length <= 500;

/**
 * Nocturne carries state with a ramp plus a written label, never colour alone:
 * the settled good outcome is plain, a payout still moving is accented, the two
 * that ended in money coming back are outlined, and "not sent" is quiet.
 */
export function payoutTone(state: PayoutState): TagTone {
  switch (state) {
    case 'PAID': return 'neutral';
    case 'SENT': case 'RETRY': case 'PROCESSING': return 'accent';
    case 'FAILED': case 'REVERSED': return 'outline';
    default: return 'quiet';
  }
}

/**
 * The queue's Payout label, or null for a request not yet released — nothing
 * has been sent anywhere, so there is nothing to say about a payout.
 *
 * A released row on a rail this platform does not send on reads NOT_SENT for
 * good, and says so in the same words the contract uses for it.
 */
export function payoutLabel(row: Pick<WithdrawalRow, 'sendable' | 'payoutState' | 'status'>): string | null {
  if (row.status !== 'RELEASED' && row.status !== 'FAILED') return null;
  if (!row.sendable && row.payoutState === 'NOT_SENT') return PAYOUT_STATE_WORDS.NOT_SENT;
  return PAYOUT_STATE_WORDS[row.payoutState];
}

/* ── The stage a request is in ─────────────────────────────────────────────── */

/**
 * The withdrawal phase, DERIVED — a deliberate choice, not a convenience.
 *
 * The design has four phases and `payment.withdrawal.status` has five values,
 * but they are not the same five: the enum is AWAITING_CHECKER · HELD_BY_RISK ·
 * RELEASED · REJECTED · FAILED, with no AWAITING_MAKER. The two pre-release
 * phases the design distinguishes therefore collapse into one column value.
 *
 * They are split here on `initiated_by`, which is exactly what the column
 * means — a row with no initiator has had no maker act on it — rather than by
 * inventing a status the database cannot hold. `withdrawal_maker_is_not_checker`
 * already encodes the other half of the rule structurally.
 *
 * HELD_BY_RISK is a fifth stage the design does not draw. It is real, it is in
 * the queue, and it is shown rather than folded into "Awaiting checker".
 */
export type Phase = 'AWAITING_MAKER' | WithdrawalRow['status'];

export function phaseOf(w: Pick<WithdrawalRow, 'status' | 'initiatedBy'>): Phase {
  if (w.status === 'AWAITING_CHECKER' && w.initiatedBy === null) return 'AWAITING_MAKER';
  return w.status;
}

export const PHASE_LABEL: Record<Phase, string> = {
  AWAITING_MAKER: 'Awaiting maker',
  AWAITING_CHECKER: 'Awaiting checker',
  HELD_BY_RISK: 'Held by risk',
  RELEASED: 'Released',
  REJECTED: 'Rejected',
  FAILED: 'Failed',
};

/** Nocturne carries state with a ramp plus a written label, never colour alone. */
export const PHASE_TONE: Record<Phase, TagTone> = {
  AWAITING_MAKER: 'accent',
  AWAITING_CHECKER: 'accent',
  HELD_BY_RISK: 'outline',
  RELEASED: 'neutral',
  REJECTED: 'outline',
  FAILED: 'outline',
};

/**
 * Where a request stands, in the stage tag's own words — for a line of text rather than a tag.
 *
 * It lives with the words, not in the screen, so that a part of the drawer can print a stage without
 * importing the screen that imports it.
 */
export function stageWords(row: Pick<WithdrawalRow, 'status' | 'initiatedBy'>): string {
  return PHASE_LABEL[phaseOf(row)];
}

/**
 * The list waiting for an operator, worked oldest park first.
 *
 * The queue's Waiting sort reads how long ago the REQUEST was made, which says
 * nothing about how long a payout has waited for a person: a request a month old
 * may have been parked an hour ago. So on that list the order is the park's.
 */
export const byParkedAt = (
  a: Pick<WithdrawalRow, 'parkedAt'>,
  b: Pick<WithdrawalRow, 'parkedAt'>,
): number =>
  (a.parkedAt === null ? Infinity : Date.parse(a.parkedAt)) -
  (b.parkedAt === null ? Infinity : Date.parse(b.parkedAt)) || 0;

/**
 * Which of the actions a parked reason allows, while the money is still in question.
 *
 * Asking StarPay again is always safe, so every reason offers it. Confirming a
 * payout as paid is refused for DRIVER_FAULT: StarPay refused the platform's
 * credentials, so no order was ever created and there is nothing to have been
 * paid. A same-id re-send only makes sense once StarPay has proved it holds no
 * order for the id (NO_ORDER_FOUND); anywhere else a second order may exist.
 */
const ALL: readonly UnparkAction[] = ['QUERY', 'CONFIRM_PAID', 'REFUND'];
export const ACTIONS_FOR_REASON: Record<ParkedReason, readonly UnparkAction[]> = {
  UNVERIFIED_ANSWER: ALL, ANSWER_DISAGREES: ALL, STATE_UNPROVEN: ALL, UNKNOWN_VOCABULARY: ALL,
  DRIVER_FAULT: ['QUERY', 'REFUND'], NO_ORDER_FOUND: [...ALL, 'RESEND_SAME_ID'], ATTEMPTS_EXHAUSTED: ALL, NO_WORD_24H: ALL, DUPLICATE_AT_BANK: ALL,
};

/** What a payout the bank returned and nobody has refunded offers, whatever it is parked for. */
const RETURNED: readonly UnparkAction[] = ['QUERY', 'REFUND'];

/**
 * Which actions a parked payout offers: its reason's, until the money is settled.
 *
 * A SETTLED PAYOUT'S HOLD HAS ONE EXIT. Once StarPay has confirmed a payout paid,
 * or the platform has refunded it (the withdrawal is FAILED then, whatever
 * StarPay's state — a refunded bank return stays REVERSED), what holds it is a
 * question no answer will settle: StarPay's word that contradicted the
 * settlement, a second order a person has since checked. Confirming it paid and
 * refunding it are both refused there (the route says why), so the row offers
 * the two that can work: asking StarPay once more, and closing the hold — which
 * moves no money. A payout still in flight is never closed: it is asked about,
 * confirmed or refunded.
 *
 * A BANK RETURN NOBODY HAS REFUNDED IS ASKED ABOUT OR REFUNDED, NEVER CONFIRMED
 * PAID. StarPay's state is REVERSED and the withdrawal is still RELEASED. Nothing
 * moves forward from REVERSED, so the route refuses Confirm paid there whatever
 * the reason it is parked for; and its hold is not closed, because the money is
 * not settled until it is refunded.
 */
export function actionsFor(
  payout: { parkedReason: ParkedReason; state: PayoutState },
  status: WithdrawalRow['status'],
): readonly UnparkAction[] {
  const settled = payout.state === 'PAID' || status === 'FAILED';
  if (settled) return ['QUERY', 'DISMISS_HOLD'];
  return payout.state === 'REVERSED' ? RETURNED : ACTIONS_FOR_REASON[payout.parkedReason];
}

/**
 * The actions that decide what became of a release, and so are a second person's: the
 * admin who released the payout may not take them (`releasedByMe`; the route refuses
 * them too, with MAKER_IS_CHECKER). Closing a hold is one, though it moves no money: a
 * hold exists so that somebody other than the releaser looks at a contradiction.
 */
export const SECOND_PERSON_ACTIONS: ReadonlySet<UnparkAction> = new Set(['CONFIRM_PAID', 'REFUND', 'DISMISS_HOLD']);

/**
 * What each action is called, what its confirmation asks, and what it does —
 * said BEFORE the press. Warn is the two that move no money, the query and
 * closing a settled payout's hold; danger is the three that move money or
 * settle the row on a person's word, none of which the platform can take back.
 *
 * THE SAME-ID RE-SEND'S DIALOG OPENS ONLY WHILE THE CONTROL IS ON — the button
 * is off, with the reason beside it, until the flag is — so its sentence says
 * what makes the re-send safe, never that the control is switched off.
 */
export const ACTION_WORDS: Record<UnparkAction, { button: string; title: string; consequence: string; tone: 'warn' | 'danger' }> = {
  QUERY: { button: 'Ask StarPay now', title: 'Ask StarPay about this payout?', consequence: 'StarPay is asked what it holds for this hand-over. A final answer moves the row; anything else leaves it exactly as it is. No money moves on the platform’s side.', tone: 'warn' },
  CONFIRM_PAID: { button: 'Confirm paid', title: 'Confirm this payout as paid?', consequence: 'The row is marked Paid on your word, with your reason. Do this only after StarPay has confirmed the transfer to you directly — the platform has no answer saying so. The player’s history will read Paid.', tone: 'danger' },
  REFUND: { button: 'Refund the player', title: 'Refund this payout to the player?', consequence: 'The amount is returned to the player’s balance now, the row is closed as failed, and the player is emailed. If StarPay later pays the bank as well, the platform has paid twice — confirm with StarPay first.', tone: 'danger' },
  RESEND_SAME_ID: { button: 'Resend with the same id', title: 'Resend this payout with the same id?', consequence: 'The same hand-over id is sent to StarPay once more, and only once. What makes this safe is that StarPay refuses an id it has already been given, so no second order can come of it — the UAT observation this control waited for before it was switched on.', tone: 'danger' },
  DISMISS_HOLD: { button: 'Close this hold', title: 'Close this hold?', consequence: 'The payout leaves the list waiting for an operator. No money moves: a paid payout stays paid, a refunded one stays refunded, and nobody is emailed. Close it only once you have checked what held it — for a possible second payment, with StarPay.', tone: 'warn' },
};

/* ── How an action's outcome is told ───────────────────────────────────────── */

/**
 * The outcomes that mean the money did not get where it was going, and the error
 * dialog's title for each. The sentence under it is the API's own.
 */
const NOT_THROUGH: Partial<Record<ReleaseOutcome['outcome'], (ref: string) => string>> = {
  FAILED_REFUNDED: (ref) => `${ref} was not paid`,
  PARKED: (ref) => `${ref} is waiting for an operator`,
  NO_ANSWER: (ref) => `StarPay has not answered about ${ref} yet`,
};

/**
 * TELL WHAT AN ACTION DID: a toast for a success, the error dialog for a payout that
 * did not get through.
 *
 * A release, a query and a same-id re-send report what StarPay did with the money,
 * and a payout refused and refunded, parked for a person or not yet answered is not
 * a success, whatever the route's status code — a toast scrolls away, and an operator
 * who misses "waiting for an operator" acts on a false belief. Those open the error
 * dialog with the API's sentence. Confirming paid, refunding and closing a hold are
 * the operator's own decision, done: a refund answers FAILED_REFUNDED because that is
 * where the row now stands, and it is a success. A refusal (an HTTP error) is the
 * error dialog's already, in the caller.
 */
export function tellOutcome(
  act: 'RELEASE' | UnparkAction,
  ref: string,
  told: ReleaseOutcome,
  tell: { toast: (message: string) => void; showError: (detail: ErrorDetail) => void },
): 'toast' | 'error' {
  const notThrough =
    act === 'RELEASE' || act === 'QUERY' || act === 'RESEND_SAME_ID' ? NOT_THROUGH[told.outcome] : undefined;
  if (notThrough) {
    tell.showError({ title: notThrough(ref), message: told.sentence });
    return 'error';
  }
  tell.toast(told.sentence);
  return 'toast';
}

/* ── The hand-over table ───────────────────────────────────────────────────── */

/** The path an answer came by, in words. */
const SOURCE_WORDS: Record<PayoutAnswerSource, string> = {
  SEND: 'reply to the send',
  QUERY: 'answer when we asked for its status',
  NOTIFICATION: 'notification',
};

/**
 * What a hand-over's Answer cell says: the latest word about it (`latestAnswer`), in
 * the API's words, with the path it came by and when — a query's answer as well as the
 * send's own reply, so a payout StarPay confirmed through "Ask StarPay now" no longer
 * reads "no answer yet" on its only hand-over. With nothing recorded, a payout still in
 * flight has no answer yet; a settled one (paid, refunded, returned) has none recorded,
 * which is what that means — an operator confirmed it, or it predates the record.
 */
export function handOverAnswer(
  attempt: Pick<PayoutAttemptRow, 'latestAnswer'>,
  state: PayoutState,
): { words: string; source: string | null; at: string | null } {
  const latest = attempt.latestAnswer;
  if (latest !== null) {
    return { words: latest.words, source: SOURCE_WORDS[latest.source], at: stamp.format(new Date(latest.at)) };
  }
  const settled = state === 'PAID' || state === 'FAILED' || state === 'REVERSED';
  return { words: settled ? 'no answer recorded' : 'no answer yet', source: null, at: null };
}

/*
 * The InstaPay codes that are the platform's own problem rather than the
 * player's, said as what they mean for the operator. Everything else is
 * StarPay's message as received.
 *
 * NEITHER SENTENCE NAMES A CONTROL THAT DOES NOT EXIST. The over-the-limit one
 * sends the operator to the rail's maximum on the Payments screen — each rail
 * row's Limits (RailLimitsDialog.tsx), and `advice-names-a-real-control` holds
 * the two together. Nothing reloads the bank list on demand — the
 * payout-banks.refresh job applies StarPay's list every hour — so the other says
 * that instead.
 */
const OVER_THE_LIMIT = new Set(['AM02', 'AM13']);
const BANK_UNREACHABLE = new Set(['RC01', 'RC02', 'RC04']);

/**
 * The Payout panel's Cause row, or null where there is nothing to explain.
 *
 * STARPAY'S ACCEPTANCE IS NEVER A CAUSE. The row keeps the message of the last
 * answer that carried one, and an answer StarPay accepted — code "200", no
 * error code — carries its acceptance ("success"): the hand-over it took, the
 * query it answered. A payout parked after that, for a day's silence, an answer
 * that could not be verified or three hand-overs, still holds it, and shown as
 * the cause it would read "Cause: success" beside Confirm paid, as if StarPay
 * had said the money arrived. So the message is a cause only when the answer
 * that left it was not an acceptance: it named an error code, or its code was
 * not "200" (compared as the string StarPay sends). A payout waiting to be sent
 * again, failed or returned shows its message on the same terms — a bank return
 * parked for a person can carry StarPay's "success" too. A parked payout with
 * nothing else to say shows no cause: the parked notice and the park's own
 * sentence say why it waits.
 */
export function causeOf(
  p: Pick<WithdrawalPayout, 'state' | 'providerCode' | 'providerMessage' | 'providerErrorCode'>,
): string | null {
  const code = p.providerErrorCode;
  if (code !== null && OVER_THE_LIMIT.has(code)) {
    return 'StarPay refused the amount as over the InstaPay limit — this rail’s maximum is set too high. Lower the rail’s maximum on the Payments screen (the rail’s Limits) to what StarPay allows; until then a payout this large fails the same way.';
  }
  if (code !== null && BANK_UNREACHABLE.has(code)) {
    return 'The bank could not be reached — the name the bank list holds for this bank may be stale; the list refreshes from StarPay every hour.';
  }
  const refused = p.providerCode !== null && p.providerCode !== '200';
  const accepted = code === null && p.providerCode === '200';
  const troubled =
    code !== null || refused || p.state === 'RETRY' || p.state === 'FAILED' || p.state === 'REVERSED';
  return troubled && !accepted ? (p.providerMessage ?? code) : null;
}

/*
 * THREE ROWS OF THE PAYOUT PANEL SAY WHAT IS TRUE OF A VALUE A PAYOUT DOES NOT HAVE: StarPay's
 * reference (its order number), the InstaPay reference and the fee. A row that reads "yet" makes a
 * promise, and on a payout that has ended it has to be one the platform keeps. The platform knows
 * only what StarPay reported, so a row with nothing to show never says more than that: "none
 * reported" where nothing more is coming, and "yet" only while something is. "None reported" makes
 * no promise, so it stays true if an operator's "Ask StarPay now" brings a value after all: the row
 * then shows it.
 *
 *   · A refunded payout (FAILED) and a returned one (REVERSED) are settled; the platform asks
 *     nothing more about them on its own.
 *   · A paid payout's InstaPay reference is not in the answer to the send. The platform asks StarPay
 *     for it afterwards, ONCE (the reconcile job's phase D), and that answer can bring the order
 *     number and the fee as well. So a paid payout waits for them only while that ask is still to
 *     come (`instapayRefAskSpent` false, the contract says when it is spent); once it is spent,
 *     nothing asks again.
 *   · A payout parked because StarPay refused the hand-over (DRIVER_FAULT: a credential or
 *     configuration fault) was refused before any order existed, so no order number, bank answer or
 *     fee is coming; the way out is restarting payouts, asking StarPay and refunding. That holds
 *     while it is still SENT (or RETRY): once StarPay reports the order with the bank (PROCESSING),
 *     the bank's answer is coming after all.
 *   · Any other payout is still moving, and waits for the bank's answer.
 *
 * A value that was given is shown whatever the state.
 */

/** Which payout driver the funds screen knows is on: the provider's status and the summary both name it. */
export type PayoutDriver = PayoutProviderStatus['driverName'];

/** StarPay refused this payout's hand-over before any order existed, and nothing since has shown one. */
const refusedBeforeAnyOrder = (
  p: Pick<WithdrawalPayout, 'state' | 'parkedAt' | 'parkedReason'>,
): boolean =>
  p.parkedAt !== null &&
  p.parkedReason === 'DRIVER_FAULT' &&
  (p.state === 'SENT' || p.state === 'RETRY');

const REFUSED_HAND_OVER = 'none — StarPay refused the hand-over';

/** Whether nothing will report a missing value now: the payout is settled, or paid with its one ask spent. */
const nothingMoreComes = (p: Pick<WithdrawalPayout, 'state' | 'instapayRefAskSpent'>): boolean =>
  p.state === 'FAILED' || p.state === 'REVERSED' || (p.state === 'PAID' && p.instapayRefAskSpent);

/**
 * The Payout panel's StarPay reference row: the order number StarPay gave, or what is true of a
 * payout that has none. "None reported" rather than "no order was created": a payout an operator
 * refunded while it was in flight may have an order whose number the platform never learned.
 */
export function providerRefWords(
  p: Pick<
    WithdrawalPayout,
    'state' | 'providerRef' | 'instapayRefAskSpent' | 'parkedAt' | 'parkedReason'
  >,
): string {
  if (p.providerRef !== null) return p.providerRef;
  if (nothingMoreComes(p)) return 'none reported';
  if (refusedBeforeAnyOrder(p)) return REFUSED_HAND_OVER;
  return 'none yet';
}

/**
 * The Payout panel's InstaPay reference row: the reference StarPay gave, or what is true of a
 * payout that has none.
 *
 * A refunded payout did not complete, and neither did a returned one StarPay never confirmed paid:
 * they say so. A payout the bank returned after StarPay confirmed it paid did complete — the Paid row
 * above says when — and the bank sent it back, so it says that. A paid payout says the platform asks
 * StarPay for its reference while that ask is still to come, and that StarPay gave none once it is
 * spent: "not yet" would wait for a reference the platform no longer asks for.
 *
 * WITH STARPAY PAYOUTS SWITCHED OFF NOTHING ASKS (the OFF driver: the job's ask selects nothing), so
 * a paid payout's ask is said to come when they are switched on. Said only when the screen knows the
 * driver (`driver`); while neither of its reads has landed it is null, and the row keeps the sentence
 * for a driver that asks — a window that lasts until the first read answers, or for good if both fail.
 */
export function instapayRefWords(
  p: Pick<
    WithdrawalPayout,
    'state' | 'instapayRef' | 'instapayRefAskSpent' | 'paidAt' | 'parkedAt' | 'parkedReason'
  >,
  driver: PayoutDriver | null,
): string {
  if (p.instapayRef !== null) return p.instapayRef;
  if (p.state === 'REVERSED' && p.paidAt !== null) return 'none — the bank returned the payout';
  if (p.state === 'FAILED' || p.state === 'REVERSED') return 'none — the payout was not completed';
  if (p.state === 'PAID') {
    if (p.instapayRefAskSpent) return 'none — StarPay did not give one';
    return driver === 'OFF'
      ? 'not yet — asked for when StarPay payouts are switched on'
      : 'not yet — the platform asks StarPay for it';
  }
  if (refusedBeforeAnyOrder(p)) return REFUSED_HAND_OVER;
  return 'not yet — arrives with the bank’s answer';
}

/**
 * The Payout panel's Fee row: the fee StarPay reported, in pesos, or what is true of a payout that
 * has none recorded. Never "not charged": the platform cannot know what StarPay did not report.
 */
export function feeWords(
  p: Pick<
    WithdrawalPayout,
    'state' | 'channelFee' | 'instapayRefAskSpent' | 'parkedAt' | 'parkedReason'
  >,
): string {
  if (p.channelFee !== null) return formatPesos(BigInt(p.channelFee));
  return nothingMoreComes(p) || refusedBeforeAnyOrder(p) ? 'none reported' : 'none reported yet';
}

/**
 * The note under the Payout panel's "Last asked": how many asks in a row brought nothing the
 * platform could use — no reply at all, a reply it could not verify or that described another
 * payout, or a code that settles nothing. A "no order" StarPay is believed about is an answer, and
 * resets the count.
 *
 * The count takes in asks nothing answered, so the line never says an answer arrived ("the last
 * answer could not be used" would); and an ask counted here may well have been answered, so it
 * never says StarPay did not answer either. "No usable answer" is true of all three. Nor is it said
 * as a failure of the payout, which these asks say nothing about.
 */
export function queryFailureWords(n: number): string {
  return n === 1
    ? 'StarPay gave no usable answer to the last ask'
    : `StarPay gave no usable answer to the last ${formatCount(n)} asks`;
}

/**
 * The "account holder's name" check in the review drawer — a real check now.
 *
 * The holder's name is compared with the name on the player's verified ID when
 * the account is registered; a mismatch waits for a second person, and the
 * release is refused until one accepts it. A request raised before registered
 * accounts existed has no holder name at all, and says so rather than passing.
 *
 * WHAT A MISMATCHED ROW'S ACCOUNT CAN BE NOW. A withdrawal is raised only
 * against an ACTIVE account, and a mismatch is ACTIVE only on a reviewer's word
 * (the database refuses it otherwise); a verdict is given once. So the account
 * behind a mismatched row was accepted by a reviewer, and the one standing it
 * can reach afterwards is RETIRED — the player may retire it once nothing is in
 * flight to it. Its check reads as accepted, with the retirement said. REFUSED
 * cannot be reached either (only an account waiting for review is refused), but
 * it is answered in words true of it rather than left to the waiting-for-review
 * sentence, which stays for PENDING_REVIEW — a standing no withdrawal row
 * reaches today.
 */
export function nameMatchCheck(row: Pick<WithdrawalRow, 'nameMatch' | 'payoutAccountStatus'>): { state: 'ok' | 'attention' | 'unknown'; value: string; note: string } {
  if (row.nameMatch === null) return { state: 'unknown', value: 'not recorded', note: 'Raised before registered accounts; no holder name was ever recorded for it.' };
  if (row.nameMatch === 'MATCHED') return { state: 'ok', value: 'Matched the verified identity automatically', note: 'The holder’s name was compared with the name on the player’s verified ID when the account was registered.' };
  if (row.payoutAccountStatus === 'ACTIVE') return { state: 'ok', value: 'Did not match; accepted by a reviewer', note: 'A second person compared the two names and accepted the account, with a recorded reason.' };
  if (row.payoutAccountStatus === 'RETIRED') return { state: 'ok', value: 'Did not match; accepted by a reviewer, since retired by the player', note: 'A second person compared the two names and accepted the account, with a recorded reason. The player has since retired it, so it cannot be used for a new withdrawal.' };
  if (row.payoutAccountStatus === 'REFUSED') return { state: 'attention', value: 'Did not match; refused by a reviewer', note: 'A second person compared the two names and refused the account, and a refusal is final. The platform sends nothing to a refused account.' };
  return { state: 'attention', value: 'Did not match; waiting for review', note: 'This release will be refused until a reviewer accepts the account on the Payout accounts tab.' };
}
